import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MainRoutes } from "common/config";
import {
  Build as WrenchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as TimesCircleIcon,
  Warning as ExclamationCircleIcon,
  Download as DownloadIcon,
  Science as FlaskIcon
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { Button } from "common/components";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import { ExternalApiClient } from "common/services/external-api-client.service";
import {
  Tool,
  TestCase
} from "modules/configuration-hub/data/models/configuration-hub.model";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import "./AvailableTools.scss";

const ToolValidation: React.FC = () => {
  const navigate = useNavigate();
  const { toolId } = useParams<{ toolId: string }>();
  const location = useLocation();
  const tool = location.state?.tool as Tool;

  // Local state for validation
  const [validatingTool, setValidatingTool] = useState<Tool | null>(null);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [validationRun, setValidationRun] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [batchId, setBatchId] = useState("");

  // Initialize component with tool data
  useEffect(() => {
    if (tool) {
      const newBatchId = `BATCH_${Date.now().toString().slice(-6)}`;
      setBatchId(newBatchId);
      setValidatingTool(tool);
      setValidationRun(false);

      // Initialize with one test case
      const firstDocId = generateDocId();
      setTestCases([
        {
          doc_id: firstDocId,
          document_type: "Test Case 1",
          fields:
            tool.inputFields?.map((fieldName) => ({
              field_name: fieldName,
              field_value: ""
            })) || []
        }
      ]);
    } else {
      // If no tool data, redirect back
      navigate(MainRoutes.AVAILABLE_TOOLS);
    }
  }, [tool, navigate]);

  const generateDocId = () => {
    return "TC-" + Math.random().toString(36).substring(2, 9).toUpperCase();
  };

  const handleUpdateField = (
    docId: string,
    fieldIndex: number,
    value: string
  ) => {
    setTestCases(
      testCases.map((tc) => {
        if (tc.doc_id === docId) {
          const updatedFields = [...tc.fields];
          updatedFields[fieldIndex] = {
            ...updatedFields[fieldIndex],
            field_value: value
          };
          return { ...tc, fields: updatedFields };
        }
        return tc;
      })
    );
  };

  const handleRunValidation = async () => {
    const hasEmptyFields = testCases[0].fields.some(
      (field) => !field.field_value.trim()
    );

    if (hasEmptyFields) {
      ToastMessageUtil.show(
        "Please fill in all field values",
        ToastVariant.ERROR
      );
      return;
    }

    setIsValidating(true);

    try {
      // Build field dictionary for API call
      const fieldDict: Record<string, string> = {};
      testCases[0].fields.forEach((field) => {
        fieldDict[field.field_name] = field.field_value;
      });

      // Call validation API using ExternalApiClient for authentication
      const validationApiUrl = `${process.env.REACT_APP_ENG_API_URL}/api/Documents/validate/tool/${toolId}`;
      const validationResponse = await ExternalApiClient.post(
        validationApiUrl,
        fieldDict,
        {
          "Content-Type": "application/json"
        }
      );

      if (validationResponse.statusCode !== 200) {
        throw new Error(
          `Validation API failed: ${validationResponse.statusCode}`
        );
      }

      const validationData = validationResponse.value as any;

      if (!validationData.isSucceed) {
        throw new Error("Validation request failed");
      }

      // Poll the result URL until completion
      const resultUrl = validationData.message;
      let result = null;
      let attempts = 0;
      const maxAttempts = 15; // Maximum 15 attempts (30 seconds with 2 second intervals)

      while (attempts < maxAttempts) {
        try {
          const resultResponse = await ExternalApiClient.get(resultUrl);
          const resultData = resultResponse.value as any;

          if (resultData && resultData.runtimeStatus === "Completed") {
            result = resultData;
            break;
          } else if (resultData && resultData.runtimeStatus === "Failed") {
            // Handle failed validation - stop polling and show failed status
            const failedTestCase = {
              ...testCases[0],
              status: "Fail" as const,
              comment: "Validation process failed during execution"
            };

            setTestCases([failedTestCase]);
            setValidationRun(true);
            setIsValidating(false);

            ToastMessageUtil.show(
              "Validation process failed",
              ToastVariant.ERROR
            );
            return; // Exit the function early
          }
        } catch (pollError) {
          // Continue to the next attempt instead of breaking
          console.warn(`Poll attempt ${attempts + 1} failed:`, pollError);
        }

        // Wait 2 seconds before next attempt
        await new Promise((resolve) => setTimeout(resolve, 2000));
        attempts++;
      }

      if (!result) {
        throw new Error("Polling timeout: Unable to get validation result");
      }

      // Process the validation results
      let overallValid = true;
      let combinedComment = "";

      // Extract validation results from the output
      if (result.output) {
        const outputValues = Object.values(result.output);
        if (outputValues.length > 0) {
          const firstResult = outputValues[0] as any;
          overallValid = firstResult.is_valid;
          combinedComment = firstResult.comment || "Validation completed";
        }
      }

      const validatedTestCase = {
        ...testCases[0],
        status: overallValid ? ("Pass" as const) : ("Fail" as const),
        comment: combinedComment
      };

      setTestCases([validatedTestCase]);
      setValidationRun(true);
      setIsValidating(false);

      if (overallValid) {
        ToastMessageUtil.show("Test case passed!", ToastVariant.SUCCESS);
      } else {
        ToastMessageUtil.show("Test case failed", ToastVariant.WARNING);
      }
    } catch (error) {
      console.error("Validation error:", error);
      setIsValidating(false);

      // Show error test case
      const errorTestCase = {
        ...testCases[0],
        status: "Fail" as const,
        comment: `Validation error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      };

      setTestCases([errorTestCase]);
      setValidationRun(true);

      ToastMessageUtil.show(
        `Validation failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        ToastVariant.ERROR
      );
    }
  };

  const handleDownloadResults = () => {
    if (!validationRun) {
      ToastMessageUtil.show("Please run validation first", ToastVariant.ERROR);
      return;
    }

    const headers = [
      "batch_id",
      "tool_name",
      "tool_description",
      "doc_id",
      "document_type",
      "field_name",
      "field_value",
      "status",
      "comment"
    ];
    const rows: string[][] = [];

    testCases.forEach((tc) => {
      tc.fields.forEach((field) => {
        rows.push([
          batchId,
          validatingTool?.displayName || "",
          validatingTool?.description || "",
          tc.doc_id,
          tc.document_type,
          field.field_name,
          field.field_value,
          tc.status || "",
          tc.comment || ""
        ]);
      });
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${batchId}_validation_results.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    ToastMessageUtil.show(
      "Results downloaded successfully",
      ToastVariant.SUCCESS
    );
  };

  const handleReworkTool = () => {
    if (validatingTool) {
      navigate(MainRoutes.TOOL_DEVELOPMENT, {
        state: {
          toolName: validatingTool.displayName,
          toolDescription: validatingTool.description
        }
      });
    }
  };

  const handleRevalidateTestCase = () => {
    // Reset validation state and clear all field values to start fresh
    setValidationRun(false);
    setTestCases(
      testCases.map((tc) => ({
        ...tc,
        status: undefined,
        comment: undefined,
        fields: tc.fields.map((field) => ({
          ...field,
          field_value: ""
        }))
      }))
    );

    ToastMessageUtil.show(
      "Test case reset. Please enter new field values and validate again.",
      ToastVariant.INFO
    );
  };

  const handleApproveTool = async () => {
    if (!validationRun) {
      ToastMessageUtil.show(
        "Please run validation before approving",
        ToastVariant.ERROR
      );
      return;
    }

    if (!validatingTool || !toolId) return;

    const testPassed = testCases[0].status === "Pass";

    if (!testPassed) {
      ToastMessageUtil.show(
        "Test case failed. Approving anyway...",
        ToastVariant.WARNING
      );
    }

    try {
      // Call approve tool API
      const approveApiUrl = `${process.env.REACT_APP_ENG_API_URL}/api/Documents/tools/${toolId}/approve`;
      const approveResponse = await ExternalApiClient.put(
        approveApiUrl,
        { toolGuid: toolId },
        {
          "Content-Type": "application/json"
        }
      );

      if (approveResponse.statusCode !== 200) {
        throw new Error(`Approve API failed: ${approveResponse.statusCode}`);
      }

      // Get existing tools from localStorage and update
      const savedTools = localStorage.getItem("customValidationTools");
      if (savedTools) {
        try {
          const tools = JSON.parse(savedTools);
          const updatedTools = tools.map((t: Tool) =>
            t.id === validatingTool.id
              ? { ...t, status: "approved" as const }
              : t
          );
          localStorage.setItem(
            "customValidationTools",
            JSON.stringify(updatedTools)
          );
        } catch (e) {
          console.error("Failed to update tools", e);
        }
      }

      ToastMessageUtil.show(
        "Tool approved successfully.",
        ToastVariant.SUCCESS
      );

      // Navigate back to available tools
      navigate(MainRoutes.AVAILABLE_TOOLS);
    } catch (error) {
      console.error("Tool approval error:", error);
      ToastMessageUtil.show(
        `Failed to approve tool: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        ToastVariant.ERROR
      );
    }
  };

  const handleCloseValidation = () => {
    navigate(MainRoutes.AVAILABLE_TOOLS);
  };

  if (!validatingTool) {
    return null; // or loading spinner
  }

  const testPassed =
    validationRun && testCases.length > 0 && testCases[0].status === "Pass";

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="available-tools">
        <HubHeader
          className="header"
          title="Validate Tool"
          description="Validate your tool with data to verify tool accuracy & performance"
          showBackButton={true}
          backRoute="/extraction-config/available-tools"
        />
        <div className="available-tools__container">
          <div className="available-tools__validation-header">
            <div className="available-tools__validation-header-content">
              <div className="available-tools__validation-header-icon">
                <WrenchIcon className="icon" />
              </div>
              <div className="available-tools__validation-header-text">
                <h1>{validatingTool.displayName}</h1>
                <p>{validatingTool.description}</p>
              </div>
            </div>
          </div>

          {/* Test Cases Section */}
          <div className="available-tools__validation-test-cases">
            <div className="available-tools__validation-test-cases-header">
              <FlaskIcon className="icon" />
              <div>
                <h2>Test Cases</h2>
                {/* <p>Add test cases to validate your tool's performance</p> */}
              </div>
            </div>

            {/* Single Test Case Display */}
            {testCases.length > 0 && (
              <div
                className={`available-tools__validation-test-case ${
                  testCases[0].status === "Pass"
                    ? "available-tools__validation-test-case--pass"
                    : testCases[0].status === "Fail"
                    ? "available-tools__validation-test-case--fail"
                    : ""
                }`}
              >
                {/* Test Case Header */}
                <div
                  className={`available-tools__validation-test-case-header ${
                    testCases[0].status === "Pass"
                      ? "available-tools__validation-test-case-header--pass"
                      : testCases[0].status === "Fail"
                      ? "available-tools__validation-test-case-header--fail"
                      : ""
                  }`}
                >
                  <div className="available-tools__validation-test-case-header-left">
                    <div
                      className={`available-tools__validation-test-case-header-number ${
                        testCases[0].status === "Pass"
                          ? "available-tools__validation-test-case-header-number--pass"
                          : testCases[0].status === "Fail"
                          ? "available-tools__validation-test-case-header-number--fail"
                          : "available-tools__validation-test-case-header-number--default"
                      }`}
                    >
                      1
                    </div>
                    <div className="available-tools__validation-test-case-header-info">
                      <h3>{testCases[0].document_type}</h3>
                      {/* <p>{testCases[0].doc_id}</p> */}
                    </div>

                    {testCases[0].status && (
                      <div
                        className={`status-badge ${
                          testCases[0].status === "Pass"
                            ? "status-badge--pass"
                            : "status-badge--fail"
                        }`}
                      >
                        {testCases[0].status === "Pass" ? (
                          <>
                            <CheckCircleIcon className="icon" /> Pass
                          </>
                        ) : (
                          <>
                            <TimesCircleIcon className="icon" /> Failed
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Fields Table */}
                <div className="available-tools__validation-test-case-body">
                  <div className="available-tools__validation-test-case-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Field Name</th>
                          <th>Field Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testCases[0].fields.map((field, fieldIndex) => (
                          <tr key={fieldIndex}>
                            <td>
                              <span>{field.field_name}</span>
                            </td>
                            <td>
                              <input
                                className="input"
                                placeholder="Enter value"
                                value={field.field_value}
                                onChange={(e) =>
                                  handleUpdateField(
                                    testCases[0].doc_id,
                                    fieldIndex,
                                    e.target.value
                                  )
                                }
                                disabled={isValidating}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Test Case Comment */}
                  {testCases[0].comment && (
                    <div
                      className={`available-tools__validation-test-case-comment ${
                        testCases[0].status === "Pass"
                          ? "available-tools__validation-test-case-comment--pass"
                          : "available-tools__validation-test-case-comment--fail"
                      }`}
                    >
                      {testCases[0].status === "Pass" ? (
                        <CheckCircleIcon className={`icon icon--pass`} />
                      ) : (
                        <ExclamationCircleIcon className={`icon icon--fail`} />
                      )}
                      <div className="available-tools__validation-test-case-comment-content">
                        <p>Comment:</p>
                        <p
                          className={
                            testCases[0].status === "Pass"
                              ? "available-tools__validation-test-case-comment-content--pass"
                              : "available-tools__validation-test-case-comment-content--fail"
                          }
                          style={{ color: "black" }}
                        >
                          {testCases[0].comment}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Re-validate Button for Failed Test Cases */}
                  {testCases[0].status === "Fail" && (
                    <div className="available-tools__validation-test-case-revalidate">
                      <Button
                        onClick={handleRevalidateTestCase}
                        className="btn btn--primary btn--small"
                        style={{
                          marginTop: "12px",
                          marginLeft: "8px"
                        }}
                      >
                        <FlaskIcon className="icon" />
                        Validate Again
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Validate Button */}
            {!validationRun && testCases.length > 0 && (
              <div className="available-tools__validation-validate-button">
                <Button
                  onClick={handleRunValidation}
                  className={`btn btn--primary btn--large ${
                    isValidating ? "disabled" : ""
                  }`}
                  disabled={isValidating}
                >
                  {isValidating ? (
                    <>
                      <CircularProgress
                        size={16}
                        className="icon animate-spin"
                      />
                      Validating...
                    </>
                  ) : (
                    <>
                      <FlaskIcon className="icon" />
                      Validate Tool
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {validationRun && (
            <div className="available-tools__validation-actions">
              <div className="available-tools__validation-actions-left">
                <Button
                  onClick={handleDownloadResults}
                  className="btn btn--secondary"
                >
                  <DownloadIcon className="icon" />
                  Download Results
                </Button>
                <Button
                  onClick={handleReworkTool}
                  className="btn btn--secondary"
                >
                  Rework Tool
                </Button>
              </div>

              <div className="available-tools__validation-actions-right">
                <Button
                  onClick={handleApproveTool}
                  className={`btn btn--large ${
                    testPassed ? "btn--success" : "btn--warning"
                  }`}
                >
                  <CheckCircleIcon className="icon" />
                  {testPassed ? "Approve Tool" : "Approve Anyway"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ToolValidation;
